import React, { useState } from 'react';
import { ResumeIcon, AnalysisIcon, InsightsIcon, RadarIcon } from './components/FeatureIcons';

// Valid promo codes
const PROMO_CODES = {
};

const FEATURE_CONFIG = {
  resume: {
    icon: <ResumeIcon size={32} />,
    title: 'Unlock More Tailored Resumes',
    description: 'Generate AI-tailored resumes that highlight your best fit for each role.',
    requiredTier: 'pro',
    freeLimit: '5/month',
    proLimit: 'Unlimited',
    highlight: 'pro',
  },
  interview_prep: {
    icon: <AnalysisIcon size={32} />,
    title: 'Unlock Interview Prep',
    description: 'Get AI-generated STAR examples and a one-sheet prep document with company insights, likely questions, and strategy.',
    requiredTier: 'pro',
    highlight: 'pro',
  },
  interview_coaching: {
    icon: <AnalysisIcon size={32} />,
    title: 'Unlock AI Interview Coaching',
    description: 'Practice with an interactive AI coach. Get real-time feedback, mock interviews, and personalized advice.',
    requiredTier: 'pro',
    highlight: 'pro',
  },
  development: {
    icon: <InsightsIcon size={32} />,
    title: 'Unlock Skill Development',
    description: 'Get personalized project recommendations and skill development planning.',
    requiredTier: 'pro',
    highlight: 'pro',
  },
  job_radar: {
    icon: <RadarIcon size={32} />,
    title: 'Unlock Job Radar',
    description: 'Track companies and get notified daily when they post new jobs matching your target roles.',
    requiredTier: 'pro',
    highlight: 'pro',
  },
};

export default function SubscriptionGate({ onSubscribe, onDismiss, feature = 'resume', currentTier = 'free' }) {
  const [loading, setLoading] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const config = FEATURE_CONFIG[feature] || FEATURE_CONFIG.resume;

  const handleApplyPromo = () => {
    const code = promoCode.trim().toUpperCase();
    if (PROMO_CODES[code]) {
      setAppliedPromo(PROMO_CODES[code]);
      setPromoError('');
    } else {
      setPromoError('Invalid promo code');
      setAppliedPromo(null);
    }
  };

  const handleSubscribe = async (plan) => {
    setLoading(plan);
    try {
      // Pass promo code if applied
      await onSubscribe(plan, appliedPromo?.discount);
    } catch (err) {
      console.error('Subscription error:', err);
      setLoading(null);
    }
  };

  // Only show Free and Pro tiers (Plus removed)

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '640px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            background: config.highlight === 'pro' ? '#fdf4d7' : '#fff3e0',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '24px',
          }}>
            {config.icon}
          </div>

          <h2 style={{
            fontSize: '22px',
            fontWeight: '600',
            color: '#2d2d2d',
            margin: '0 0 8px 0',
          }}>
            {config.title}
          </h2>

          <p style={{
            fontSize: '14px',
            color: '#5a5a5a',
            margin: 0,
            lineHeight: '1.5',
          }}>
            {config.description}
          </p>
        </div>

        {/* Pricing Grid - Free and Pro only */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: currentTier === 'free' ? '1fr 1fr' : '1fr',
          gap: '12px',
          marginBottom: '20px',
        }}>
          {/* Free Tier - only show if currently free */}
          {currentTier === 'free' && (
            <div style={{
              padding: '16px',
              background: '#f5f5f5',
              borderRadius: '12px',
              border: '2px solid #e0e0e0',
              opacity: 0.7,
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#7a7a7a',
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                Free
              </div>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#7a7a7a',
                marginBottom: '10px',
              }}>
                $0
              </div>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                fontSize: '11px',
                color: '#7a7a7a',
              }}>
                <li style={{ padding: '2px 0' }}>✓ Unlimited job analysis</li>
                <li style={{ padding: '2px 0' }}>✓ 5 resumes/month</li>
                <li style={{ padding: '2px 0' }}>✓ Job tracker</li>
                <li style={{ padding: '2px 0', color: '#ccc' }}>✗ Interview prep</li>
                <li style={{ padding: '2px 0', color: '#ccc' }}>✗ Skill development</li>
              </ul>
              <div style={{
                marginTop: '12px',
                padding: '8px',
                fontSize: '11px',
                color: '#7a7a7a',
                textAlign: 'center',
              }}>
                Current Plan
              </div>
            </div>
          )}

          {/* Pro Plan */}
          <div style={{
            padding: '16px',
            background: config.highlight === 'pro' ? '#fffbf0' : '#fafafa',
            borderRadius: '12px',
            border: `2px solid ${config.highlight === 'pro' ? '#f7c948' : '#e0e0e0'}`,
            position: 'relative',
          }}>
            {config.requiredTier === 'pro' && (
              <div style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#f7c948',
                color: '#3d3d3d',
                fontSize: '9px',
                fontWeight: '600',
                padding: '3px 8px',
                borderRadius: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                Unlocks This
              </div>
            )}
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#d4a832',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Pro
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#2d2d2d',
              marginBottom: '4px',
            }}>
              <span style={{ textDecoration: 'line-through', color: '#9a9a9a', fontSize: '16px' }}>$50</span>
              {' '}$25
              <span style={{ fontSize: '12px', fontWeight: '400', color: '#7a7a7a' }}>/mo</span>
            </div>
            <div style={{
              fontSize: '11px',
              color: '#d4736a',
              fontWeight: '600',
              marginBottom: '8px',
            }}>
              50% off for 3 months (ALPHAVIP)
            </div>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              fontSize: '11px',
              color: '#5a5a5a',
            }}>
              <li style={{ padding: '2px 0' }}>✓ Unlimited job analysis</li>
              <li style={{ padding: '2px 0' }}>✓ Unlimited resumes</li>
              <li style={{ padding: '2px 0' }}>✓ Job tracker</li>
              <li style={{ padding: '2px 0', fontWeight: feature === 'interview_coaching' ? '600' : '400' }}>✓ Interview prep & AI coaching</li>
              <li style={{ padding: '2px 0', fontWeight: feature === 'development' ? '600' : '400' }}>✓ Skill development</li>
              <li style={{ padding: '2px 0', color: '#d4a832', fontStyle: 'italic', fontSize: '10px' }}>✦ Job alerts (coming soon)</li>
            </ul>
            <button
              onClick={() => handleSubscribe('pro')}
              disabled={loading !== null || currentTier === 'pro'}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '10px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#2d2d2d',
                background: loading === 'pro' ? '#ccc' : currentTier === 'pro' ? '#aaa' : '#f7c948',
                border: 'none',
                borderRadius: '8px',
                cursor: loading !== null || currentTier === 'pro' ? 'not-allowed' : 'pointer',
              }}
            >
              {currentTier === 'pro' ? 'Current Plan' : loading === 'pro' ? 'Redirecting...' : 'Get Alpha Access'}
            </button>
          </div>
        </div>

        {/* Promo Code Input */}
        <div style={{
          background: '#f8f6f0',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
        }}>
          <div style={{ fontSize: '12px', fontWeight: '500', color: '#3d3d3d', marginBottom: '8px' }}>
            Have a promo code?
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              placeholder="Enter code"
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '13px',
                border: '1px solid #d8d8d8',
                borderRadius: '6px',
                fontFamily: "'IBM Plex Sans', sans-serif",
              }}
            />
            <button
              onClick={handleApplyPromo}
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: '600',
                background: '#3d3d3d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Apply
            </button>
          </div>
          {promoError && (
            <div style={{ fontSize: '11px', color: '#d4736a', marginTop: '6px' }}>
              {promoError}
            </div>
          )}
          {appliedPromo && (
            <div style={{
              fontSize: '11px',
              color: '#5a8f5a',
              marginTop: '6px',
              padding: '8px',
              background: '#e8f5e9',
              borderRadius: '4px',
            }}>
              <strong>Code applied!</strong> {appliedPromo.description} on Pro plan
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={onDismiss}
            disabled={loading !== null}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '500',
              color: '#7a7a7a',
              background: 'transparent',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Maybe Later
          </button>

          <p style={{
            fontSize: '11px',
            color: '#9a9a9a',
            margin: '16px 0 0 0',
          }}>
            Questions?{' '}
            <a
              href="mailto:lori.aretemgmt@gmail.com?subject=Manifold Careers - Subscription Question"
              style={{ color: '#d4736a', textDecoration: 'none' }}
            >
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
